/**
 * Types and helpers for user-drawn structures (proposed buildings) on the
 * parcel. These are distinct from the OSM-observed `buildingFootprints`
 * layer (which renders existing structures from public data).
 */

/** A grayscale axis-aligned box drawn by the user inside the parcel. */
export interface PlacedStructure {
  /** Stable id, e.g. "s-1700000000000-3f". */
  id: string;
  /** Display name, e.g. "Structure 1". Auto-increments as new ones are added. */
  name: string;
  /** E/W extent in feet. */
  widthFt: number;
  /** N/S extent in feet. */
  depthFt: number;
  /** Vertical extent in feet (rendered above the terrain). */
  heightFt: number;
  /** E/W centre position in feet, from the parcel local origin (+ = east). */
  centerXFt: number;
  /** N/S centre position in feet, from the parcel local origin (+ = north). */
  centerYFt: number;
  /** Raise the box above grade in feet. 0 = sits on the terrain. */
  gradeOffsetFt: number;
  /**
   * Yaw rotation in degrees from default orientation. Negative = counterclockwise
   * when viewed from above; positive = clockwise. 0° = width along E/W, depth
   * along N/S. Range ±180°.
   */
  rotationDeg: number;
  /** Whether the per-structure section is expanded in the inspector card. */
  expanded: boolean;
  /** When true, the box cannot be moved on the parcel or edited until unlocked. */
  locked: boolean;
}

/**
 * Live pointer state for the structures tool. Drives both the preview mesh
 * and the cmd-shift-4-style cursor readout.
 *
 * All values are in feet from the parcel local origin; the layer converts
 * to world meters when it renders.
 */
export type StructuresInteraction =
  | { kind: "idle" }
  | {
      kind: "drawing";
      startXFt: number;
      startYFt: number;
      currentXFt: number;
      currentYFt: number;
      /** Pinned default height for the in-progress draw (see plan). */
      heightFt: number;
    }
  | {
      kind: "moving";
      id: string;
      pointerStartXFt: number;
      pointerStartYFt: number;
      originalCenterXFt: number;
      originalCenterYFt: number;
      currentCenterXFt: number;
      currentCenterYFt: number;
    };

export interface StructuresState {
  list: PlacedStructure[];
  interaction: StructuresInteraction;
  /** Top-level card expand/collapse (per Figma). */
  expandedCard: boolean;
  /**
   * True while the user has clicked "Add structure" but hasn't yet completed
   * a draw drag. Drives the modal crosshair cursor.
   */
  drawModeActive: boolean;
}

/** Default vertical height for new structures, in feet (matches Figma). */
export const DEFAULT_STRUCTURE_HEIGHT_FT = 15;

/** Minimum side length (in feet) that counts as a real structure on pointerup. */
export const MIN_STRUCTURE_SIDE_FT = 3;

/** Grade-offset slider bounds in feet (symmetric around terrain surface). */
export const MIN_GRADE_OFFSET_FT = -20;
export const MAX_GRADE_OFFSET_FT = 20;

/** Rotation slider bounds in degrees (center = 0, ±180). */
export const MIN_ROTATION_DEG = -180;
export const MAX_ROTATION_DEG = 180;

/** Clamp signed yaw to the inspector slider range. */
export function clampRotationDeg(deg: number): number {
  const n = Math.round(deg);
  return Math.min(MAX_ROTATION_DEG, Math.max(MIN_ROTATION_DEG, n));
}

/**
 * Map signed UI degrees (negative = CCW) to Three.js +Y yaw radians.
 */
export function structureRotationRad(rotationDeg: number): number {
  return (-rotationDeg * Math.PI) / 180;
}

/**
 * Convert legacy 0–359° storage (positive = CCW) to signed ±180°
 * (negative = CCW).
 */
export function migrateLegacyRotationDeg(deg: number): number {
  let n = -Math.round(deg);
  n = ((n + 180) % 360 + 360) % 360 - 180;
  return n;
}

export function defaultStructuresState(): StructuresState {
  return {
    list: [],
    interaction: { kind: "idle" },
    expandedCard: true,
    drawModeActive: false
  };
}

/** Mint a sortable, unique id without pulling in `uuid`. */
export function makeStructureId(): string {
  const rand = Math.random().toString(16).slice(2, 8);
  return `s-${Date.now().toString(36)}-${rand}`;
}

/** Sequential default name; pass the current `list`. */
export function nextStructureName(list: readonly PlacedStructure[]): string {
  let n = list.length + 1;
  const used = new Set(list.map((s) => s.name));
  while (used.has(`Structure ${n}`)) n += 1;
  return `Structure ${n}`;
}

/**
 * Exclusive selection for the Structures panel editor: `expanded` is true
 * for at most ONE structure. Toggling the selected one deselects it;
 * selecting another collapses the rest.
 */
export function toggleStructureSelectionExclusive(
  list: readonly PlacedStructure[],
  id: string
): PlacedStructure[] {
  const target = list.find((s) => s.id === id);
  const nextExpanded = target ? !target.expanded : false;
  return list.map((s) => ({
    ...s,
    expanded: s.id === id ? nextExpanded : false
  }));
}

/** Select exactly `id` (used after add / auto-place so the editor opens on the new one). */
export function selectStructureExclusive(
  list: readonly PlacedStructure[],
  id: string
): PlacedStructure[] {
  return list.map((s) => ({ ...s, expanded: s.id === id }));
}

/** Ground footprint area in ft² (width × depth in feet); horizontal footprint of the oriented box. */
export function structureFootprintSqFt(
  s: Pick<PlacedStructure, "widthFt" | "depthFt">
): number {
  const a = Number(s.widthFt) * Number(s.depthFt);
  return Number.isFinite(a) && a >= 0 ? a : 0;
}
